import _ from "lodash";
import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { AccessToken, AppState, DateRangeDateTime, DateShift, Equipment, EquipmentAttributes, EquipmentAttributesById, Event, EventsByPersonnelsResponseItem, EventsCalculationItem, EventsCalculationItemExtended, EventsResponseItem, FShift, Person, PersonByIdAttributes, PersonCard, Shift, StatusCalculations, STP } from "../types/types";
import { DateTime } from "luxon";
import { DateRange } from "react-day-picker";
import { ColumnFiltersState, GroupingState, SortingState } from "@tanstack/react-table";
import { extendEquipmentTimelineData, extendPersonTimelineData, getEquipmentBreaksViolation, getEquipmentInteractions, getPersonInteractions } from "../lib/calculations";

type TabView = "table" | "report" | "calendar" | "settings";

interface TabProperties {
  id: string;
  title: string;
  active?: boolean;
  favicon?: boolean | string;
  faviconClass?: string;
}

export interface Tab extends TabProperties {
  type: TabType;
  url: string;
  dataIsLoaded: boolean;
  dataIsLoading: boolean;
  template: TemplateType;
  view?: TabView;
  options?: TabOptions;
  reportOptions: TabReportOptions;
  defaultValues?: DefaultValues;
}

export interface TabReportOptions {
  type: TabReportOptionsType;
  items: string[];
}

export type TabReportOptionsType = "summary" | "item" | null;

export interface EquipmentBlockFilters {
  days: string[];
  weekdays: string[];
  shifts: string[];
  equipments: string[];
}

export interface PersonBlockFilters {
  days: string[];
  weekdays: string[];
  shifts: string[];
  equipments: string[];
  persons: string[]
}

export interface EquipmentTemplate {
  name: string;
  slug: string;
  data: EquipmentTemplateBlock[]
}

export type TemplateType = "equipments_idle_report" | "equipments_free_template" | "persons_free_template" | "divisions_free_template";

export interface EquipmentTemplateBlock {
  id?: number;
  name: string;
  type: EquipmentReportBlockType;
  functions: string[] | undefined;
  position: BlockPosition;
  options: {
    parameters: WidgetParameter[];
    base_parameter: WidgetParameter;
    x_parameter: XParameter,
    y_parameter: YParameter,
    split_by: SplitBy;
    active_rows: boolean;
    date_time_range?: DateRangeDateTime;
  },
  filters?: EquipmentBlockFilters | undefined,
  content: string | undefined
}

// export type TabOptions = EquipmentTabOptions | EquipmentsTabOptions | PersonsTabOptions;

// export type TabOptions = EquipmentTabOptions | EquipmentsTabOptions;

export type TabType = "equipments" | "persons" | "divisions";

export interface TabOptions {
  report_type: TabType;
  params: string;
  reportData: DataRow[];
  tableData: EquipmentReportTableData[] | PersonReportTableData[];
  filters?: TabFilters;
  blocks?: Array<EquipmentReportBlock> | Array<PersonReportBlock>;
  savedTabData?: {
    name: string;
    title: string | undefined;
    access_type: string;
  };
}

export interface TabFilters {
  excludeWeekend?: boolean;
  grouping?: GroupingState;
  sorting?: SortingState;
  filters?: ColumnFiltersState;
  categories?: any;
}

export interface EventsResponseItemWithShift extends EventsResponseItem {
  shift: FShift;
}

export interface EventsByPersonnelsResponseItemWithShiftAndPerson extends EventsByPersonnelsResponseItem {
  shift: FShift;
  person: Person;
}

export type DataRow = {
  id: number;
  hidden: boolean;
} & EventsCalculationItem & EventsCalculationItemExtended

export type UsageRow = {
  dateShift: DateShift;
  interactionIds: number[];
  interactions: UsageRowInteraction[]
  start: DateTime;
  startWork: DateTime;
  end: DateTime;
  endWork: DateTime;
  total: {total: STP, worked: STP, stopped: STP, on: STP, off: STP};
  availability: {total: STP, worked: STP, stopped: STP, on: STP, off: STP};
  unavailability: {total: STP, worked: STP, stopped: STP, on: STP, off: STP};
  works: {total: STP, worked: STP, stopped: STP, on: STP, off: STP};
  maintenance: {total: STP, worked: STP, stopped: STP, on: STP, off: STP};
  repair: {total: STP, worked: STP, stopped: STP, on: STP, off: STP};
  broken: {total: STP, worked: STP, stopped: STP, on: STP, off: STP};
  service: {total: STP, worked: STP, stopped: STP, on: STP, off: STP};
  other: {total: STP, worked: STP, stopped: STP, on: STP, off: STP};
  
}

export type UsageRowInteraction = {
  person: Person,
  card: PersonCard
}

export type SummaryUsageRow = {
  interactionIds: number[];
  total: {total: STP, worked: STP, stopped: STP, on: STP, off: STP};
  availability: {total: STP, worked: STP, stopped: STP, on: STP, off: STP};
  unavailability: {total: STP, worked: STP, stopped: STP, on: STP, off: STP};
  works: {total: STP, worked: STP, stopped: STP, on: STP, off: STP};
  maintenance: {total: STP, worked: STP, stopped: STP, on: STP, off: STP};
  repair: {total: STP, worked: STP, stopped: STP, on: STP, off: STP};
  broken: {total: STP, worked: STP, stopped: STP, on: STP, off: STP};
  service: {total: STP, worked: STP, stopped: STP, on: STP, off: STP};
  other: {total: STP, worked: STP, stopped: STP, on: STP, off: STP};
  data: UsageRow[]
}

export type PersonUsageRow = {
  dateShift: DateShift;
  interactionIds: number[];
  cardIds: number[];
  start: DateTime;
  startWork: DateTime;
  end: DateTime;
  endWork: DateTime;
  interaction: {total: STP, worked: STP, stopped: STP, on: STP, off: STP};
  manual: {total: STP, worked: STP, stopped: STP, on: STP, off: STP};
  maintenance: {total: STP, worked: STP, stopped: STP, on: STP, off: STP};
  repair: {total: STP, worked: STP, stopped: STP, on: STP, off: STP};
  service: {total: STP, worked: STP, stopped: STP, on: STP, off: STP};
  interactions: PersonUsageRowInteraction[]
}

export type PersonUsageRowInteraction = {
  equipment: Equipment;
  start: DateTime;
  startWork: DateTime;
  end: DateTime;
  endWork: DateTime;
  availability: {total: STP, worked: STP, stopped: STP, on: STP, off: STP};
  unavailability: {total: STP, worked: STP, stopped: STP, on: STP, off: STP};
  works: {total: STP, worked: STP, stopped: STP, on: STP, off: STP};
  maintenance: {total: STP, worked: STP, stopped: STP, on: STP, off: STP};
  repair: {total: STP, worked: STP, stopped: STP, on: STP, off: STP};
  broken: {total: STP, worked: STP, stopped: STP, on: STP, off: STP};
  service: {total: STP, worked: STP, stopped: STP, on: STP, off: STP};
  other: {total: STP, worked: STP, stopped: STP, on: STP, off: STP};
}

export type SummaryPersonUsageRow = {
  interactionIds: number[];
  cardIds: number[];
  interaction: {total: STP, worked: STP, stopped: STP, on: STP, off: STP};
  manual: {total: STP, worked: STP, stopped: STP, on: STP, off: STP};
  maintenance: {total: STP, worked: STP, stopped: STP, on: STP, off: STP};
  repair: {total: STP, worked: STP, stopped: STP, on: STP, off: STP};
  service: {total: STP, worked: STP, stopped: STP, on: STP, off: STP};
  interactions: SummaryPersonUsageRowInteraction[];
  data: PersonUsageRow[]
}

export type SummaryPersonUsageRowInteraction = {
  equipment: Equipment;
  availability: {total: STP, worked: STP, stopped: STP, on: STP, off: STP};
  unavailability: {total: STP, worked: STP, stopped: STP, on: STP, off: STP};
  works: {total: STP, worked: STP, stopped: STP, on: STP, off: STP};
  maintenance: {total: STP, worked: STP, stopped: STP, on: STP, off: STP};
  repair: {total: STP, worked: STP, stopped: STP, on: STP, off: STP};
  broken: {total: STP, worked: STP, stopped: STP, on: STP, off: STP};
  service: {total: STP, worked: STP, stopped: STP, on: STP, off: STP};
  other: {total: STP, worked: STP, stopped: STP, on: STP, off: STP};
}

export interface ExtendedEquipmentData {
  equipmentStartWork: DateTime | null;
  equipmentStartWorkDelta: DateTime | Duration | null;
  equipmentEndWork: DateTime | null;
  equipmentEndWorkDelta: DateTime | Duration | null;
}

export type ReportInteraction = {
  id: number;
  card_number: string;
  card_type: string;
  first_name: string;
  last_name: string;
  middle_name: string;
  post: string;
  at: string;
  start: DateTime;
  finish: DateTime;
  duration: DateTime;
  worked: DateTime;
  stopped: DateTime;
}

export type PersonReportTableData = {
  id: number;
  hidden: boolean;
  at: DateTime;
  atWithTime: DateTime;
  weekday: string;

  shift: FShift;
  shiftName: string;
  shiftStart: DateTime,
  shiftEnd: DateTime,
  shiftStartAsString: string;
  shiftEndAsString: string;
  shiftDuration: number;
  shiftDurationAsString: string;
  excludes?: Exclude[],
  breaks?: EquipmentBreak[],

  personnel_id: string;
  person: Person;
  person_full_name: string;
  person_first_name: string;
  person_last_name: string;
  person_middle_name: string;
  person_division_name: string | null;
  // person_division_id: string | number | null;
  person_post_name: string | null;
  person_access_token_id: string;
  // person_access_token_type: string;

  activeShift: boolean;
  activityDuration: number;
  activityDurationAsPercent: number;
  activityDurationAsString: string;
  
  worked: number;
  workedAsPercent: number;
  workedAsString: string;
  stopped: number;
  stoppedAsPercent: number;
  stoppedAsString: string;
  no_card: number;
  noCardAsPercent: number;
  noCardAsString: string;

  interaction?: PersonInteractionData[] | PersonInteractionExtendedData[];
  timeline: EquipmentTimelineData[];
  timelineByHours: {
    hour: number; 
    duration: number;
    worked: number;  
    stopped: number;  
    turned_off: number; 
    no_data: number;
  }[];
} & PersonExcludesParameters & PersonTimelineParameters;
// InteractionParameters & ExcludesParameters & TimelineParameters;

export type EquipmentReportTableData = {
  id: number;
  hidden: boolean;
  at: DateTime;
  atWithTime: DateTime;
  weekday: string;
  equipment_item_id: string;
  equipment_name: string;
  equipment_model_name: string;
  equipment_max: number | null;
  equipment_min: number | null;
  equipment_division_name: string | null;
  inventory_number: string;
  stop_threshold: number;
  worked: number;
  workedAsPercent: number;
  workedAsString: string;
  stopped: number;
  stoppedAsPercent: number;
  stoppedAsString: string;
  turned_off: number;
  turnedOffAsPercent: number;
  turnedOffAsString: string;

  shift: FShift;
  shiftName: string;
  shiftStart: DateTime,
  shiftEnd: DateTime,
  shiftStartAsString: string;
  shiftEndAsString: string;
  shiftDuration: number;
  shiftDurationAsString: string;
  activeShift: boolean;
  excludes?: Exclude[], 
  breaks?: EquipmentBreak[],

  interaction?: EquipmentInteractionData[] | EquipmentInteractionExtendedData[];
  timeline: EquipmentTimelineData[];
  timelineByHours: {
    hour: number; 
    duration: number;
    worked: number;  
    stopped: number;  
    turned_off: number; 
    no_data: number;
  }[];
} & InteractionParameters & EquipmentTimelineParameters;


export interface PointTableData {
  id: number;
  hidden: boolean;
  at: DateTime;
  weekday: string;
  equipment_item_id: string;
  equipment_name: string;
  equipment_division_name: string | null;
  inventory_number: string;

  shift: FShift;
  shiftName: string;
  shiftStart: DateTime,
  shiftEnd: DateTime,

  duration: number;
  end_at: number;
  finish: string;
  start: string;
  start_at: number;
  state: string;
  no_data_extended: {from: number, to: number, duration: number}[];
  no_data_seconds: number;

  type: string[],
  breakMessage: string,
  shiftMessage: string,
  idleMessage: string,
  violation: boolean,
  violationType: string[],
  violationValue: number,
  iteractions: (EquipmentInteractionData | EquipmentInteractionExtendedData)[]
  breaks?: EquipmentBreak[],
  iteraction: EquipmentInteractionData | EquipmentInteractionExtendedData | null
} 

export interface IteractionViewData {
  id: number;
  hidden: boolean;
  at: DateTime;
  weekday: string;
  equipment_item_id: string;
  equipment_name: string;
  equipment_division_name: string | null;
  inventory_number: string;

  shift: FShift;
  shiftName: string;
  shiftStart: DateTime,
  shiftEnd: DateTime,

  duration: number;
  finish: DateTime;
  start: DateTime;

  worked: number;
  stopped: number;

  personnel: PersonByIdAttributes;

  timeline: EquipmentTimelineData[]
}

export type EquipmentReportBlockType =  
  "pie_chart" | 
  "text_block" | 
  "header_block" |
  "summary_heat_map_block" | 
  "bar_chart" | 
  "line_chart" | 
  "heat_map" |
  "gantt" |
  "idle_table_block" | 
  "interactions_heat_map_block" | 
  "interactions_table_block" | 
  "parametric_graph_block" | 
  "power_graph_block" | 
  "calculated_block" | 
  "equipment_start_end_block";

export type CalculationType = "load" | "idle" | "repair" | "service" | "manual" | "adjustment" | "shifts" | "interaction" | "equipment_start" | "equipment_end" | "breaks";
export type WidgetParameter = 
  "availability.worked" | "availability.stopped" | "availability.total" | "availability.on" | "availability.off" |
  "unavailability.worked" | "unavailability.stopped" | "unavailability.total" | "unavailability.on" | "unavailability.off" |
  "works.worked" | "works.stopped" | "works.total" | "works.on" | "works.off" |
  "maintenance.worked" | "maintenance.stopped" | "maintenance.total" | "maintenance.on" | "maintenance.off" |
  "broken.worked" | "broken.stopped" | "broken.total" | "broken.on" | "broken.off" |
  "repair.worked" | "repair.stopped" | "repair.total" | "repair.on" | "repair.off" |
  "service.worked" | "service.stopped" | "service.total" | "service.on" | "service.off" |
  "other.worked" | "other.stopped" | "other.total" | "other.on" | "other.off" |
  "violations.start" | "violations.startWork" | "violations.startInteraction" |
  "violations.end" | "violations.endWork" | "violations.endInteraction" | 
  "duration.worked" | "duration.stopped" | "duration.on" | "duration.off" | "duration.total" 

export type SplitBy = "days" | "shifts" | "equipments" | "persons" | "weekdays" | "none"

export interface BlockPosition  {
  i: number;
  x: number;
  y: number | typeof Infinity;
  w: number;
  h: number;
  minW: number | undefined;
  minH: number | undefined;
  resizeHandles: string[];
}

export interface ReportBlockFilters {
  weekdays: Array<string>;
  days: Array<string>;
  shifts: Array<string>;
  equipments: Array<string>;
  persons: Array<string>;
}

export interface EquipmentReportBlock {
  id: number;
  name: string;
  type: EquipmentReportBlockType;
  position: BlockPosition,
  options?: {
    parameters: WidgetParameter[];
    base_parameter: WidgetParameter;
    x_parameter: XParameter;
    y_parameter: YParameter;
    split_by: SplitBy;
    // calculation_type: CalculationType;
    // split_by_days?: boolean;
    // split_by_shifts?: boolean;
    active_rows?: boolean;
    date_time_range?: DateRangeDateTime;
  },
  filters?: ReportBlockFilters;
  content?: any;
}

export interface PersonReportBlock {
  id: number;
  name: string;
  type:
    | "pie_chart"
    | "text_block"
    | "header_block"
    | "summary_heat_map_block"
    | "bar_chart"
    | "line_chart"
    | "heat_map"
    | "gantt" 
    | "idle_table_block"
    | "interactions_heat_map_block"
    | "interactions_table_block"
    | "parametric_graph_block"
    | "power_graph_block"
    | "calculated_block";
  position: BlockPosition,
  options?: {
    parameters: WidgetParameter[];
    base_parameter: WidgetParameter;
    x_parameter: XParameter;
    y_parameter: YParameter;
    split_by: SplitBy;
    // calculation_type: "load" | "idle";
    // split_by_days?: boolean;
    // split_by_shifts?: boolean;
    active_rows?: boolean;
    date_time_range?: DateRangeDateTime;
  },
  filters?: ReportBlockFilters;
  content?: any;
}

export type XParameter = "days" | "hours" | null
export type YParameter = "days" | "equipments" | null

export interface EquipmentsReport {
  equipments: EquipmentReportData[];
  shift: FShift;
}

export interface PersonsReport {
  persons: PersonReportData[];
  shift: FShift;
}

export interface PersonReportData {
  at: DateTime;
  personnel_id: number;
  stopped: number;
  no_card: number;
  worked: number;
  interaction?: PersonInteractionData[] | PersonInteractionExtendedData[];
  hidden: boolean;
}

export interface EquipmentReportData {
  at: DateTime;
  equipment_item_id: number;
  stopped: number;
  turned_off: number;
  worked: number;
  interaction?: EquipmentInteractionData[] | EquipmentInteractionExtendedData[];
  hidden: boolean;
}

export interface EquipmentTimelineData {
  at: string;
  duration: number;
  end_at: number;
  finish: string;
  start: string;
  start_at: number;
  state: string;
  equipment_item_id: number;
  no_data_extended: {from: number, to: number, duration: number}[];
  no_data_seconds: number;
}

export interface PersonTimelineData {
  at: string;
  duration: number;
  end_at: number;
  finish: string;
  start: string;
  start_at: number;
  state: string;
  personnel_id: number;
  no_card_seconds: number;
  no_card_extended: {from: number, to: number, duration: number}[];
}

export interface EquipmentInteractionData {
  equipment_item?: any;
  at: string;
  duration: number;
  equipment_item_id: number;
  shift: string;
  finish: string;
  personnel_id: number;
  start: string;
  total_duration: number;
  total_finish: string;
  total_start: string;
  personnel: PersonByIdWithId;
}

export interface EquipmentInteractionExtendedData extends EquipmentInteractionData {
  equipmentStart: DateTime;
  equipmentEnd: DateTime;
  worked: number;
  stopped: number;
  turnedOff: number;
}

export interface EquipmentBreak {
  hasViolation: boolean,
  violation: number,
  breakStart: DateTime,
  breakEnd: DateTime,
  duration: number,
  worked: number,
  stopped: number,
  turned_off: number
}

export interface PersonInteractionData {
  at: string;
  duration: number;
  equipment_item_id: number;
  equipment_item?: EquipmentAttributesById;
  finish: string;
  personnel_id: number;
  start: string;
}

export interface PersonInteractionExtendedData extends PersonInteractionData {
  equipmentStart: DateTime;
  equipmentEnd: DateTime;
  worked: number;
  stopped: number;
  turnedOff: number;
}

export interface InteractionParameters {
  interactionsAsDuration: number | undefined,
  interactionsAsPercent: number | undefined,
  interactionsAsString: string | undefined,
  workedInInteractions: number | undefined,
  workedInInteractionsAsPercent: number | undefined,
  workedInInteractionsAsString: string | undefined,
  stoppedInInteractions: number | undefined,
  stoppedInInteractionsAsPercent: number | undefined,
  stoppedInInteractionsAsString: string | undefined,
  turnedOffInInteractions: number | undefined,
  turnedOffInInteractionsAsPercent: number | undefined,
  turnedOffInInteractionsAsString: string | undefined
}

export interface ExcludesParameters {
  workedWithExcludes: number,
  workedWithExcludesAsPercent: number,
  workedWithExcludesAsString: string,
  stoppedWithExcludes: number,
  stoppedWithExcludesAsPercent: number,
  stoppedWithExcludesAsString: string,
  turnedOffWithExcludes: number,
  turnedOffWithExcludesAsPercent: number,
  turnedOffWithExcludesAsString: string,
  excludesViolation: number | undefined,
  shiftDurationWithoutExcludes: number,
  shiftDurationWithoutExcludesAsString: string,
  workedInExcludes: number | undefined,
  workedInExcludesAsPercent: number | undefined,
  workedInExcludesAsString: string | undefined,
  stoppedInExcludes: number | undefined,
  stoppedInExcludesAsPercent: number | undefined,
  stoppedInExcludesAsString: string | undefined,
  turnedOffInExcludes: number | undefined,
  turnedOffInExcludesAsPercent: number | undefined,
  turnedOffInExcludesAsString: string | undefined,
}

export interface PersonExcludesParameters {
  workedCalculatedAsPercent: number,
  workedWithExcludes: number,
  workedWithExcludesAsPercent: number,
  workedWithExcludesAsString: string,
  stoppedWithExcludes: number,
  stoppedWithExcludesAsPercent: number,
  stoppedWithExcludesAsString: string,
  noCardWithExcludes: number,
  noCardWithExcludesAsPercent: number,
  noCardWithExcludesAsString: string,
  excludesViolation: number | undefined,
  shiftDurationWithoutExcludes: number,
  shiftDurationWithoutExcludesAsString: string,
  workedInExcludes: number | undefined,
  workedInExcludesAsPercent: number | undefined,
  workedInExcludesAsString: string | undefined,
  stoppedInExcludes: number | undefined,
  stoppedInExcludesAsPercent: number | undefined,
  stoppedInExcludesAsString: string | undefined,
  noCardInExcludes: number | undefined,
  noCardInExcludesAsPercent: number | undefined,
  noCardInExcludesAsString: string | undefined,
}

export interface EquipmentTimelineParameters {
  timelineIsLoaded: boolean;
  equipmentStartedAt: DateTime | undefined;
  equipmentStartedAtAsString: string | undefined;
  equipmentStartViolation: number | undefined;
  equipmentStartAsStringViolation: number | undefined;
  equipmentEndedAt: DateTime | undefined;
  equipmentEndedAtAsString: string | undefined;
  equipmentEndViolation: number | undefined;
  equipmentEndViolationAsString: string | undefined;
}

export interface PersonTimelineParameters {
  timelineIsLoaded: boolean;
  firstHourWorked: number | undefined;
  firstHourStopped: number | undefined;
  lastHourWorked: number | undefined;
  lastHourStopped: number | undefined;
}

interface PersonByIdWithId extends PersonByIdAttributes {
  id: number;
}

export interface DefaultValues {
  equipment?: { value: string; label: string };
  equipments?: { value: string; label: string }[];
  person?: { value: string; label: string };
  persons?: { value: string; label: string }[];
  shift?: { value: string; label: string };
  shifts?: FShift[];
  period_type: "static" | "dynamic";
  dynamic_period: number | undefined;
  dates?: DateRange | undefined;
  excludes?: Exclude[];
}

export interface Exclude {
  exclude_start: string;
  exclude_end: string;
  shift: string;
}

export interface EquipmentWork {
  duration: number;
  equipment_item_id: number;
  no_data: number;
  state: string;
  time_end: DateTime;
  time_start: DateTime;
}

export interface UsageData {
  at: string;
  equipment_item_id: number;
  stopped: number;
  turned_off: number;
  worked: number;
}

export interface PersonUsageData {
  at: string;
  personnel_id: number;
  stopped: number;
  no_card: number;
  worked: number;
}

interface TabsState {
  activeReport: {title: string, name: string | TabType | null} | null;
  tabs: Array<Tab>;
  activeTabId: string | null;
  setActiveReport: (report: {title: string, name: string | TabType | null}) => void;
  setActiveTab: (tabId: string | null) => void;
  changeTabView: (tabId: string, view: TabView, reportType: TabReportOptionsType, reportItems: string[]) => void;
  changeTabDataIsLoaded: (tabId: string, loadState: boolean, loadingState: boolean) => void;
  changeTabTitle: (tabId: string, title: string) => void;
  changeTabTemplate: (tabId: string, template: TemplateType) => void;
  addTab: (tab: Tab) => void;
  closeTab: (tabId: string) => void;
  reorderTabs: (tabId: string, fromIndex: number, toIndex: number) => void;
  setTabs: (tabs: Array<Tab>) => void;
  addDefaultValues: (tabId: string, cache: DefaultValues) => void;
  addFilters: (tabId: string, filters: TabFilters) => void;
  addBlocks: (tabId: string, blocks: EquipmentReportBlock[] | PersonReportBlock[]) => void;
  updateBlocks: (tabId: string, blocks: EquipmentReportBlock[] | PersonReportBlock[]) => void;
  editBlock: (tabId: string, blockId: number, action: "up" | "down") => void;
  editBlockContent: (tabId: string, blockId: number, content: any) => void;
  deleteCurrentData: (tabId: string) => void;
  addOptions: (tabId: string, options: TabOptions) => void;
  addEquipmentTimelineToRow: (tabId: string, rowId: number, timeline: EquipmentTimelineData[]) => void;
  addPersonTimelineToRow: (tabId: string, rowId: number, timeline: PersonTimelineData[]) => void;
  changeItemProperty: (
    tabId: string | number,
    dataID: number,
    propertyName: "timeline" | "hidden" | "firstHourWorked" | "lastHourWorked",
    value: any
  ) => void;
  resetStore: () => void;
}

export const useTabsStore = create<TabsState>()(
  immer<TabsState>((set, get) => ({
    activeReport: null,
    tabs: [],
    activeTabId: null,
    setActiveReport: (report) => set((state) => {state.activeReport = report}),
    setActiveTab: (tabId: string | null) => {
      set((state) => {
        state.activeTabId = tabId;
      });
      set((state) => {
        if(!tabId) {
          state.tabs = state.tabs.map((tab) => ({ ...tab, active: false }))
        } else {
          state.tabs = state.tabs.map((tab) => ({ ...tab, active: tabId === tab.id }));
        } 
      });
    },
    addTab: (tab: Tab) => {
      set((state) => {
        state.tabs.push(tab);
      });
      get().setActiveTab(tab.id);
    },
    closeTab: (tabId: string) => {
      if (tabId === get().activeTabId) {
        let currentTabIndex = get().tabs.findIndex((tab) => tabId == tab.id);
        let nextActiveTabId;
        if (get().tabs[currentTabIndex + 1]) nextActiveTabId = get().tabs[currentTabIndex + 1].id;
        else if (get().tabs[currentTabIndex + -1]) nextActiveTabId = get().tabs[currentTabIndex + -1].id;
        else nextActiveTabId = "";
        get().setActiveTab(nextActiveTabId);
      }
      set((state) => {
        state.tabs = state.tabs.filter((tab) => tab.id !== tabId);
      });
    },
    reorderTabs: (tabId: string, fromIndex: number, toIndex: number) => {
      const beforeTab = get().tabs.find((tab) => tab.id === tabId);
      if (beforeTab) {
        const newTabs = get().tabs.filter((tab) => tab.id !== tabId);
        newTabs.splice(toIndex, 0, beforeTab);
        set((state) => {
          state.tabs = newTabs;
        });
      }
    },
    setTabs: (tabs) =>
      set((state) => {
        state.tabs = tabs;
      }),
    changeTabTitle: (tabId, title) => 
      set((state) => {
        state.tabs.find((tab) => tab.id === tabId)!.title = title;
      }),
    changeTabTemplate: (tabId, template) => 
      set((state) => {
        state.tabs.find((tab) => tab.id === tabId)!.template = template;
      }),
    addDefaultValues: (tabId, values) =>
      set((state) => {
        state.tabs.find((tab) => tab.id === tabId)!.defaultValues = values;
        state.tabs.find((tab) => tab.id === tabId)!.dataIsLoaded = false;
      }),
    deleteCurrentData: (tabId) =>
      set((state) => {
        state.tabs.find((tab) => tab.id === tabId)!.defaultValues = undefined;
        state.tabs.find((tab) => tab.id === tabId)!.options = undefined;
        state.tabs.find((tab) => tab.id === tabId)!.dataIsLoaded = false;
      }),
    addOptions: (tabId, options) =>
      set((state) => {
        state.tabs.find((tab) => tab.id === tabId)!.options = options;
      }),
    addEquipmentTimelineToRow: (tabId, rowId, timeline) => {
      const tabIndex = get().tabs.findIndex((tab) => tab.id === tabId);
      let tab = get().tabs[tabIndex]
      let row = tab.options?.tableData[rowId - 1] as EquipmentReportTableData;
      let newRow = {
        ...row, 
        timeline: timeline,
        ...getEquipmentInteractions(row?.interaction, timeline, row?.stop_threshold ? row?.stop_threshold : 0),
        ...getEquipmentBreaksViolation(timeline, row.shiftStart, row.shiftEnd, tab.defaultValues?.excludes!),
        // ...getEquipmentParamsWithExcludes(timeline, tab.defaultValues?.excludes, row?.at!, row?.shift!, {worked: row?.worked!, stopped: row?.stopped!, turned_off: row?.turned_off!}), 
        ...extendEquipmentTimelineData(timeline, row?.at!, row?.shift!, row?.stop_threshold ? row?.stop_threshold : 0)
      }
      set((state) => {
        // @ts-ignore
        state.tabs[tabIndex]!.options!.tableData[rowId - 1] = newRow;
      });
    },
    addPersonTimelineToRow: (tabId, rowId, timeline) => {
      const tabIndex = get().tabs.findIndex((tab) => tab.id === tabId);
      let tab = get().tabs[tabIndex]
      let row = tab.options?.tableData[rowId - 1] as PersonReportTableData;
      let newRow = {
        ...row, 
        timeline: timeline,
        // ...getPersonInteractions(row?.interaction, timeline, row?.stop_threshold ? row?.stop_threshold : 0),
        // ...getPersonParamsWithExcludes(timeline, tab.defaultValues?.excludes, row?.at!, row?.shift!, {worked: row?.worked!, stopped: row?.stopped!, turned_off: row?.turned_off!}), 
        // ...extendPersonTimelineData(timeline, row?.at!, row?.shift!, row?.stop_threshold ? row?.stop_threshold : 0)
      }
      set((state) => {
        // @ts-ignore
        state.tabs[tabIndex]!.options!.tableData[rowId - 1] = newRow;
      });
    },
    changeItemProperty: (tabId, dataID, propertyName, value) =>
      set((state) => {
        // @ts-ignore
        state.tabs.find((tab) => tab.id === String(tabId))!.options!.tableData[dataID - 1]![propertyName] = value;
      }),
    addFilters: (tabId, filters) =>
      set((state) => {
        state.tabs.find((tab) => tab.id === tabId)!.options!.filters = filters;
      }),
    addBlocks: (tabId, blocks) =>
      set((state) => {
        state.tabs.find((tab) => tab.id === tabId)!.options!.blocks = blocks;
      }),
    editBlock: (tabId, blockId, action) => {
      const tabIndex = get().tabs.findIndex((tab) => tab.id === tabId);
      const blocks = get().tabs[tabIndex]!.options!.blocks;
      const currentBlock = blocks?.find((block) => block.id == blockId)!;
      const anotherBlock =
        action == "up"
          ? blocks?.find((block) => block.id == blockId - 1)!
          : blocks?.find((block) => block.id == blockId + 1)!;

      let newBlocks: EquipmentReportBlock[] | PersonReportBlock[] = [];
      if (action == "down") {
        newBlocks = [
          ...blocks?.filter((b) => ![blockId, blockId + 1].includes(b.id))!,
          ...[
            { ...currentBlock, id: blockId + 1 },
            { ...anotherBlock, id: blockId },
          ],
        ];
      } else {
        newBlocks = [
          ...blocks?.filter((b) => ![blockId, blockId - 1].includes(b.id))!,
          ...[
            { ...currentBlock, id: blockId - 1 },
            { ...anotherBlock, id: blockId },
          ],
        ];
      }
      set((state) => {
        state.tabs[tabIndex]!.options!.blocks = newBlocks;
      });
    },
    updateBlocks: (tabId, blocks) => 
      set((state) => {
        state.tabs.find((tab) => tab.id === tabId)!.options!.blocks! = blocks;
      }),
    editBlockContent: (tabId, blockId, content) =>
      set((state) => {
        state.tabs.find((tab) => tab.id === tabId)!.options!.blocks!.find((b) => b.id === blockId)!.content = content;
      }),
    changeTabView: (tabId, view, reportType, reportItems) =>
      set((state) => {
        state.tabs.find((tab) => tab.id === tabId)!.view = view;
        state.tabs.find((tab) => tab.id === tabId)!.reportOptions = {type: reportType, items: reportItems};
      }),
    changeTabDataIsLoaded: (tabId, loadState, loadingState) =>
      set((state) => {
        state.tabs.find((tab) => tab.id === tabId)!.dataIsLoading = loadingState;
        state.tabs.find((tab) => tab.id === tabId)!.dataIsLoaded = loadState;
      }),
    resetStore: () =>
      set((state) => {
        state.tabs = [];
        state.activeTabId = "";
      }),
  }))
);
